/**
 * 一斉通知用カスタムフック
 *
 * Cloudflare Queues による非同期処理に対応
 * ジョブの進捗をポーリングで取得
 */

import type { BroadcastJobProgress } from "@repo/types/admin";
import { useCallback, useEffect, useRef, useState } from "react";
import { apiClient } from "@/lib/api-client";

// ============================================
// 型定義
// ============================================

interface UserSummary {
  id: string;
  email: string;
  name: string;
  isPremium: boolean;
}

interface UsersResult {
  users: UserSummary[];
  total: number;
}

interface StartBroadcastResult {
  success: true;
  jobId: string;
  totalUsers: number;
  message: string;
}

interface HistoryResult {
  jobs: BroadcastJobProgress[];
  total: number;
}

interface UseBroadcastResult {
  users: UserSummary[];
  total: number;
  isLoading: boolean;
  error: string | null;
  isSending: boolean;
  currentJob: BroadcastJobProgress | null;
  history: BroadcastJobProgress[];
  historyTotal: number;
  isLoadingHistory: boolean;
  startBroadcast: (
    subject: string,
    htmlContent: string,
    textContent: string,
  ) => Promise<StartBroadcastResult | null>;
  clearCurrentJob: () => void;
  refresh: () => Promise<void>;
  refreshHistory: () => Promise<void>;
}

// ============================================
// ポーリング設定
// ============================================

const POLL_INTERVAL = 2000; // 2秒

// ============================================
// フック
// ============================================

export function useBroadcast(): UseBroadcastResult {
  const [users, setUsers] = useState<UserSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [currentJob, setCurrentJob] = useState<BroadcastJobProgress | null>(null);
  const [history, setHistory] = useState<BroadcastJobProgress[]>([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ユーザー一覧を取得
  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiClient.api.admin.notifications.users.$get();

      if (response.ok) {
        const data = (await response.json()) as UsersResult;
        setUsers(data.users);
        setTotal(data.total);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError((errorData as { error?: string }).error || "ユーザー一覧の取得に失敗しました");
      }
    } catch (_err) {
      setError("通信エラーが発生しました");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // 履歴を取得
  const fetchHistory = useCallback(async () => {
    setIsLoadingHistory(true);

    try {
      const response = await apiClient.api.admin.notifications.broadcast.$get();

      if (response.ok) {
        const data = (await response.json()) as HistoryResult;
        setHistory(data.jobs);
        setHistoryTotal(data.total);
      }
    } catch (err) {
      console.error("Failed to fetch history:", err);
    } finally {
      setIsLoadingHistory(false);
    }
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  // ジョブ進捗を取得
  const fetchJobProgress = useCallback(
    async (jobId: string) => {
      try {
        const response = await apiClient.api.admin.notifications.broadcast[":jobId"].$get({
          param: { jobId },
        });

        if (response.ok) {
          const progress = (await response.json()) as BroadcastJobProgress;
          setCurrentJob(progress);

          // 完了したらポーリング停止 & 履歴を更新
          if (progress.status === "completed" || progress.status === "failed") {
            if (pollIntervalRef.current) {
              clearInterval(pollIntervalRef.current);
              pollIntervalRef.current = null;
            }
            setIsSending(false);
            // 履歴を再取得
            fetchHistory();
          }
        }
      } catch (err) {
        console.error("Failed to fetch job progress:", err);
      }
    },
    [fetchHistory],
  );

  // ポーリング開始
  const startPolling = useCallback(
    (jobId: string) => {
      // 既存のポーリングをクリア
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }

      // 即座に1回取得
      fetchJobProgress(jobId);

      // ポーリング開始
      pollIntervalRef.current = setInterval(() => {
        fetchJobProgress(jobId);
      }, POLL_INTERVAL);
    },
    [fetchJobProgress],
  );

  // クリーンアップ
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  // 一斉送信を開始
  const startBroadcast = useCallback(
    async (
      subject: string,
      htmlContent: string,
      textContent: string,
    ): Promise<StartBroadcastResult | null> => {
      setIsSending(true);
      setError(null);

      try {
        const response = await apiClient.api.admin.notifications.broadcast.$post({
          json: { subject, htmlContent, textContent },
        });

        if (response.ok) {
          const result = (await response.json()) as StartBroadcastResult;

          // 初期進捗を設定
          setCurrentJob({
            jobId: result.jobId,
            status: "processing",
            totalUsers: result.totalUsers,
            sentCount: 0,
            failedCount: 0,
            failedEmails: [],
            subject,
            startedAt: new Date().toISOString(),
          });

          // ポーリング開始
          startPolling(result.jobId);

          return result;
        }

        const errorData = await response.json().catch(() => ({}));
        setError((errorData as { error?: string }).error || "一斉送信の開始に失敗しました");
        setIsSending(false);
        return null;
      } catch (_err) {
        setError("通信エラーが発生しました");
        setIsSending(false);
        return null;
      }
    },
    [startPolling],
  );

  // 現在のジョブをクリア
  const clearCurrentJob = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    setCurrentJob(null);
    setIsSending(false);
  }, []);

  return {
    users,
    total,
    isLoading,
    error,
    isSending,
    currentJob,
    history,
    historyTotal,
    isLoadingHistory,
    startBroadcast,
    clearCurrentJob,
    refresh: fetchUsers,
    refreshHistory: fetchHistory,
  };
}
